import { Injectable, effect, signal, untracked } from '@angular/core';
import { AuthorizationModel } from '../../models/data/authorization.model';
import { AuthorizationService } from './authorization.service';
import { UserSessionService } from '../user-session.service';
import { GuardConstants } from '../../constants/constants';
import { ServiceResultModel } from '../../models/service/service-result.model';
import { ServiceUtilities } from '../../utilities/service.utilities';

@Injectable({
  providedIn: 'root'
})
export class AuthorizationStateService {
  public authorizationState = signal<AuthorizationModel | undefined>(undefined);

  // Raised when a load has definitively failed — transient retries exhausted, or a terminal status
  // such as 401/403. userLoadedGuard watches this so it can redirect as soon as the load is known to
  // have failed, instead of waiting out the rest of AUTHORIZATION_STATE_TIMEOUT_MS for state that is
  // never going to arrive.
  public authorizationLoadFailed = signal<boolean>(false);

  private previousUserId: number | undefined = undefined;

  constructor(
    private userSessionService: UserSessionService,
    private authorizationService: AuthorizationService
  ) {
    this.initializeAuthorizationEffect();
  }

  
  private initializeAuthorizationEffect(): void {
    effect(() => {
      const user = this.userSessionService.activeUser();
      const currentUserId = user?.userId || undefined;

      if (currentUserId !== this.previousUserId) {
        this.previousUserId = currentUserId;

        if (user && user.userId) {
          untracked(() => {
            void this.loadContext(user.userId);
          });
        } else {
          this.clearContext();
        }
      }
    });
  }


  private async loadContext(userId: number): Promise<void> {
    this.authorizationLoadFailed.set(false);

    try {
      const result = await this.fetchRolesAndPermissions(userId);

      if (result.success && result.data) {
        this.authorizationState.set(result.data);
        return;
      }
    } catch (error) {
      // getUserRolesAndPermissions swallows its own errors, so this only catches an unexpected throw —
      // which still has to raise the failure flag, or the guard waits out its full timeout for nothing.
      console.error('Error loading user roles and permissions:', error);
    }

    // clearContext() resets the flag, so it has to be raised after that call, not before.
    this.clearContext();
    this.authorizationLoadFailed.set(true);
  }


  // A transient blip on this call would otherwise leave the authorization state unset and strand a
  // user with a valid Auth0 token on /system/auth-error, since userLoadedGuard gates the whole
  // dashboard on it. The call reports failure through its result rather than by throwing, so the
  // retry keys off that.
  private fetchRolesAndPermissions(userId: number): Promise<ServiceResultModel<AuthorizationModel>> {
    return ServiceUtilities.retryTransient(
      () => this.authorizationService.getUserRolesAndPermissions(userId),
      GuardConstants.AUTH_LOAD_MAX_RETRIES(),
      GuardConstants.AUTH_LOAD_RETRY_BASE_DELAY_MS(),
      'AuthorizationStateService.getUserRolesAndPermissions'
    );
  }


  // Runs mid-session (e.g. after a membership change), so unlike the initial load it only ever sets
  // on success: dropping the live authorization state over a blip would be worse than holding the
  // current snapshot. It leaves authorizationLoadFailed alone for the same reason — that flag is the
  // guard's sign-in signal, and the user is already past the guard by now.
  public async refreshContext(): Promise<void> {
    const user = this.userSessionService.activeUser();
    if (user?.userId) {
      const result = await this.fetchRolesAndPermissions(user.userId);
      if (result.success && result.data) {
        this.authorizationState.set(result.data);
      }
    }
  }

  public clearContext(): void {
    this.authorizationState.set(undefined);
    this.authorizationLoadFailed.set(false);
    this.previousUserId = undefined;
  }
}
